import { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { BAR_HEIGHT } from "./plannerGeometry"

// A late bar keeps its own palette and takes a red outline, rather than being
// repainted red wholesale — the tone still tells you what kind of work it is.
export const LATE_BAR_TONE =
  "border-red-400 bg-red-50 text-red-950 dark:border-red-500/70 dark:bg-red-950/50 dark:text-red-100"

// The bar whose order is open in the detail panel. z-[11] clears the other bars
// and the now-line but stays under the sticky vehicle column (z-20), which
// shares this stacking context and has to keep covering bars scrolled behind
// it. Selection owns `ring` outright — lateness uses border colour — so the two
// can never fight over ring width.
export const SELECTED_BAR_HIGHLIGHT =
  "z-[11] -translate-y-px shadow-md ring-2 ring-blue-600 ring-offset-1 ring-offset-background dark:ring-blue-500"

type TimelineBarProps = {
  left: number
  top: number
  width: number
  /** Already composed by barLabel() — "REF · route", or either half alone. */
  label: string
  title: string
  /** Palette classes for this kind of work (chain tone, planned, dispatcher). */
  tone: string
  startFlag?: string | null
  endFlag?: string | null
  isSelected?: boolean
  isLate?: boolean
  /** Renders in place of the end flag — the route bar's loading spinner. */
  trailing?: ReactNode
  /**
   * Share of the work behind the driver, 0..1, from chainProgress(). Null or
   * undefined leaves the bar unfilled — we only shade what we can vouch for.
   */
  progress?: number | null
  onClick?: () => void
  disabled?: boolean
  className?: string
}

/**
 * One bar on the planner timeline. Carries the shape the mockup asks for
 * (rounded, bordered, shadowed pill) so the four kinds of bar — provider
 * chains, the Slick route bar, planned orders and dispatcher-parked orders —
 * can't drift apart. Absolutely positioned inside a row's timeline track.
 */
const TimelineBar = ({
  left,
  top,
  width,
  label,
  title,
  tone,
  startFlag,
  endFlag,
  isSelected,
  isLate,
  trailing,
  progress,
  onClick,
  disabled,
  className,
}: TimelineBarProps) => {
  const percentage =
    typeof progress === "number" ? Math.round(progress * 100) : null
  const content = (
    <>
      {percentage !== null && (
        <span
          aria-hidden
          data-testid="timeline-bar-progress"
          // Shading rather than its own colour, so the fill reads on every
          // tone — late, current, done — without a palette per state. The
          // right edge is the part dispatchers compare with the now-line.
          className="pointer-events-none absolute inset-y-0 left-0 rounded-l-md border-r border-black/25 bg-black/10 dark:border-white/30 dark:bg-white/15"
          style={{ width: `${percentage.toString()}%` }}
        />
      )}
      <span className="relative min-w-0 truncate">
        {startFlag && <span aria-hidden>{startFlag} </span>}
        {label}
      </span>
      {trailing ??
        (endFlag && (
          <span aria-hidden className="relative shrink-0">
            {endFlag}
          </span>
        ))}
    </>
  )

  const classes = cn(
    "absolute flex items-center justify-between gap-1 overflow-hidden rounded-md border px-1.5 text-left text-[11px] font-medium leading-none shadow-sm",
    isLate ? LATE_BAR_TONE : tone,
    onClick && "hover:brightness-95",
    isSelected && SELECTED_BAR_HIGHLIGHT,
    className,
  )
  const style = { left, top, width, height: BAR_HEIGHT }
  // The fill has no label of its own, so the tooltip is where the number lives.
  const tooltip =
    percentage === null ? title : `${title} · ${percentage.toString()}%`

  if (!onClick) {
    return (
      <div className={classes} style={style} title={tooltip}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      className={classes}
      style={style}
      title={tooltip}
      disabled={disabled}
      onClick={onClick}
    >
      {content}
    </button>
  )
}

export default TimelineBar
